/**
 * Image utility functions for category image processing
 */

export interface ImageDimensions {
  width: number;
  height: number;
}

/**
 * Check browser support for required canvas features
 */
export const checkCanvasSupport = (): { supported: boolean; missingFeatures: string[] } => {
  const missingFeatures: string[] = [];

  // Check for basic canvas support
  if (!document.createElement('canvas').getContext) {
    missingFeatures.push('Canvas API');
  }

  // Check for 2D context support
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    missingFeatures.push('Canvas 2D Context');
  }

  // Check for toDataURL support
  if (!canvas.toDataURL) {
    missingFeatures.push('Canvas toDataURL');
  }

  // Check for atob/btoa support (base64 encoding)
  if (typeof atob === 'undefined' || typeof btoa === 'undefined') {
    missingFeatures.push('Base64 encoding (atob/btoa)');
  }

  // Check for Blob support
  if (typeof Blob === 'undefined') {
    missingFeatures.push('Blob API');
  }

  return {
    supported: missingFeatures.length === 0,
    missingFeatures
  };
};

export interface ImageProcessingOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;  // 0.1 to 1.0
  format?: 'jpeg' | 'png' | 'webp';
}

/**
 * Validate image file before upload
 */
export const validateImageFile = (file: File): { isValid: boolean; error?: string } => {
  // Check file type
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/svg+xml'];
  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: 'Invalid file type. Please upload JPEG, PNG, WebP, or SVG images only.'
    };
  }

  // Check file size (5MB limit)
  const maxSize = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSize) {
    return {
      isValid: false,
      error: 'File size too large. Please upload images smaller than 5MB.'
    };
  }

  return { isValid: true };
};

/**
 * Get image dimensions from file
 */
export const getImageDimensions = (file: File): Promise<ImageDimensions> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
};

/**
 * Resize and optimize image
 */
export const processImage = (
  file: File, 
  options: ImageProcessingOptions = {}
): Promise<Blob> => {
  const {
    maxWidth = 1200,
    maxHeight = 600,
    quality = 0.85,
    format = 'jpeg'
  } = options;

  return new Promise((resolve, reject) => {
    // Check browser support first
    const browserSupport = checkCanvasSupport();
    if (!browserSupport.supported) {
      reject(new Error(`Browser missing required features: ${browserSupport.missingFeatures.join(', ')}`));
      return;
    }

    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Canvas 2D context not available'));
      return;
    }

    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img;
      
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      if (height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
      }

      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;

      // Draw and compress image
      ctx.drawImage(img, 0, 0, width, height);

      try {
        // Use toDataURL approach for more reliable MIME type handling
        const dataUrl = canvas.toDataURL(`image/${format}`, quality);
        
        if (!dataUrl || dataUrl === 'data:,') {
          throw new Error('Canvas toDataURL returned empty result');
        }

        // Convert data URL to Blob with explicit MIME type
        const base64Data = dataUrl.split(',')[1];
        if (!base64Data) {
          throw new Error('Invalid data URL format');
        }

        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { 
          type: `image/${format}` 
        });

        // Validate the created blob
        if (blob.size === 0) {
          throw new Error('Created blob has zero size');
        }

        if (blob.type !== `image/${format}`) {
          throw new Error(`Blob type mismatch: expected image/${format}, got ${blob.type}`);
        }

        resolve(blob);
        
      } catch (error) {
        reject(new Error(`Failed to process image: ${error instanceof Error ? error.message : 'Unknown error'}`));
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for processing'));
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * Generate unique filename for category image
 */
export const generateCategoryImageFilename = (
  categoryName: string,
  variant: 'original' | 'card' | 'thumbnail' | 'hero' = 'original'
): string => {
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(2, 6); // 4 random chars
  const slug = categoryName
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  
  // Return flat filename without subdirectory, including random component
  return `${slug}-${variant}-${timestamp}-${randomStr}.jpg`;
};

/**
 * Generate multiple image variants
 */
export const generateImageVariants = async (file: File, categoryName: string) => {
  const variants = {
    original: await processImage(file, { maxWidth: 1200, maxHeight: 600, quality: 0.9 }),
    card: await processImage(file, { maxWidth: 600, maxHeight: 300, quality: 0.85 }),
    thumbnail: await processImage(file, { maxWidth: 300, maxHeight: 150, quality: 0.8 }),
    hero: await processImage(file, { maxWidth: 1200, maxHeight: 600, quality: 0.9 })
  };

  const filenames = {
    original: generateCategoryImageFilename(categoryName, 'original'),
    card: generateCategoryImageFilename(categoryName, 'card'),
    thumbnail: generateCategoryImageFilename(categoryName, 'thumbnail'),
    hero: generateCategoryImageFilename(categoryName, 'hero')
  };

  return { variants, filenames };
};

/**
 * Create image preview URL
 */
export const createImagePreviewUrl = (file: File): string => {
  return URL.createObjectURL(file);
};

/**
 * Cleanup image preview URL
 */
export const cleanupImagePreviewUrl = (url: string): void => {
  URL.revokeObjectURL(url);
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Check if file is an image
 */
export const isImageFile = (file: File): boolean => {
  return file.type.startsWith('image/');
};